import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const RAW_BASE_URL =
  process.env['NX_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
const BASE_URL = RAW_BASE_URL.endsWith('/')
  ? RAW_BASE_URL.slice(0, -1)
  : RAW_BASE_URL;

/**
 * Property definition for a datastore entity (table/view column)
 */
export interface DatastoreEntityProperty {
  name: string;
  type: string;
  nullable: boolean;
  format?: string; // e.g., "geometry", "date-time"
}

/**
 * Entity (table/view) in a datastore with metadata
 * This is protocol-agnostic and populated from Blanche's schemas table
 */
export interface DatastoreEntity {
  name: string; // Entity name for queries (e.g., "public_customers")
  originalPath: string; // Original path from schemas table (e.g., "public/customers")
  type: 'table' | 'view' | 'materialized_view';
  properties: DatastoreEntityProperty[];

  // Additional metadata from schemas table
  count?: number; // Row count
  estimatedSizeBytes?: number; // Storage size in bytes
  lastSyncedAt?: string; // ISO datetime when Dorothy last synced
  lastSeenAt?: string; // ISO datetime when Dorothy last saw the table
  exists?: boolean; // Whether the table still exists

  // Future protocol hints
  hasGeometry?: boolean; // For WFS/GeoSpatial widgets
  hasForeignKeys?: boolean; // For GraphQL widgets
  hasTimeSeries?: boolean; // For Digital Twin widgets
}

export interface DatastoreMetadata {
  entities: DatastoreEntity[];
}

/**
 * Transform JSON Schema to our property format
 */
function transformJsonSchemaToProperties(
  jsonSchema: any,
): DatastoreEntityProperty[] {
  if (!jsonSchema || !jsonSchema.properties) {
    return [];
  }

  return Object.entries(jsonSchema.properties).map(
    ([name, propDef]: [string, any]) => {
      // Handle nullable types (e.g., ["string", "null"])
      let type = propDef.type;
      let nullable = false;

      if (Array.isArray(type)) {
        nullable = type.includes('null');
        type = type.find((t: string) => t !== 'null') || 'string';
      } else {
        nullable = propDef.nullable === true;
      }

      return {
        name,
        type: type || 'string',
        nullable,
        format: propDef.format,
      };
    },
  );
}

/**
 * Detect if a JSON Schema has geometry columns
 */
function hasGeometryColumn(jsonSchema: any): boolean {
  if (!jsonSchema?.properties) return false;

  return Object.values(jsonSchema.properties).some(
    (prop: any) => prop.format === 'geometry' || prop.type === 'geometry',
  );
}

/**
 * Detect if a JSON Schema has foreign keys
 */
function hasForeignKeyColumn(jsonSchema: any): boolean {
  if (!jsonSchema?.properties) return false;

  return Object.values(jsonSchema.properties).some(
    (prop: any) => prop['x-foreign-key'] !== undefined,
  );
}

/**
 * Detect if a JSON Schema has timestamp columns (for time-series)
 */
function hasTimestampColumn(jsonSchema: any): boolean {
  if (!jsonSchema?.properties) return false;

  return Object.values(jsonSchema.properties).some(
    (prop: any) =>
      prop.format === 'date-time' ||
      prop.type === 'timestamp' ||
      prop.format === 'timestamp',
  );
}

/**
 * Transform Blanche's schemas JSON:API response to DatastoreEntity format
 */
function transformSchemasToEntities(jsonApiResponse: any): DatastoreEntity[] {
  const schemas = jsonApiResponse.data || [];

  return schemas.map((schema: any) => {
    const attributes = schema.attributes;
    const jsonSchema = attributes.json_schema || {};

    // Extract properties from JSON Schema
    const properties = transformJsonSchemaToProperties(jsonSchema);

    // Extract entity name from relative_path
    // e.g., "public/customers" → "public_customers"
    // e.g., "archive/sales_data" → "archive_sales_data"
    const relativePath = attributes.relative_path || '';
    const entityName = relativePath.replace(/\//g, '_');

    // Determine table type
    let tableType: 'table' | 'view' | 'materialized_view' = 'table';
    if (attributes.table_type === 'view') {
      tableType = 'view';
    } else if (attributes.table_type === 'materialized_view') {
      tableType = 'materialized_view';
    }

    return {
      name: entityName,
      originalPath: relativePath,
      type: tableType,
      properties,
      count: attributes.count,
      estimatedSizeBytes: attributes.estimated_size_bytes,
      lastSyncedAt: attributes.last_synced_at,
      lastSeenAt: attributes.last_seen_at,
      exists: attributes.exists !== false, // Default to true if not specified

      // Protocol hints
      hasGeometry: hasGeometryColumn(jsonSchema),
      hasForeignKeys: hasForeignKeyColumn(jsonSchema),
      hasTimeSeries: hasTimestampColumn(jsonSchema),
    };
  });
}

/**
 * Fetches protocol-agnostic schema metadata for a datastore from Blanche's schemas table
 * This replaces the OData-specific useDatastoreMetadata hook
 */
export function useDatastoreSchemas(
  accountId: string,
  datastoreId: string,
  enabled: boolean = true,
) {
  return useQuery<DatastoreMetadata>({
    queryKey: ['datastore-schemas', accountId, datastoreId],
    queryFn: async (): Promise<DatastoreMetadata> => {
      // Use Blanche's protocol-agnostic schemas endpoint
      // BASE_URL includes /api prefix in production (from NX_PUBLIC_API_BASE_URL env var)
      const url = `${BASE_URL}/accounts/${accountId}/schemas?entity_id=${datastoreId}`;

      const response = await axios.get(url, {
        withCredentials: true,
      });

      // Transform JSON:API response to our format
      const entities = transformSchemasToEntities(response.data);

      return { entities };
    },
    enabled: enabled && !!accountId && !!datastoreId,
    staleTime: 5 * 60 * 1000, // 5 minutes - schemas don't change often
    retry: 2, // Retry twice to handle race conditions with auth
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Gets the list of field names for a specific entity from schema metadata
 */
export function useEntityFields(
  accountId: string,
  datastoreId: string,
  entityName: string,
  enabled: boolean = true,
) {
  const {
    data: metadata,
    isLoading,
    error,
  } = useDatastoreSchemas(accountId, datastoreId, enabled);

  const entity = metadata?.entities.find((e) => e.name === entityName);
  const fields = entity?.properties.map((p) => p.name) || [];

  return {
    fields,
    entity,
    isLoading,
    error,
  };
}
